// Workflow Copilot — client transport.
//
// Wraps the agentic loop (mirrors doctorChat.js):
//   1. POST /workflow-copilot/chat/stream with the compressed live workflow +
//      conversation.
//   2. When the server pauses with kind === 'pending_client_tool', run the
//      tool locally (workflowCopilotTools) against the LIVE canvas — write
//      tools mutate workflowState immediately — then POST the result to
//      /chat/resume/stream with a FRESH compressed snapshot (so the model's
//      overview reflects the edit).
//   3. Repeat until kind === 'final'.

var apiClient = require('./apiClient');
var copilotTools = require('./workflowCopilotTools');

var ENDPOINT_CHAT_STREAM = '/workflow-copilot/chat/stream';
var ENDPOINT_RESUME_STREAM = '/workflow-copilot/chat/resume/stream';

// No per-turn tool limit — this is only a runaway backstop (matches the
// server's ABSOLUTE_LOOP_CEILING) so a stuck loop can't spin forever.
var MAX_CLIENT_TOOL_ROUNDS = 250;

// args:
//   getWorkflowState() → CURRENT live workflowState (re-queried every round)
//   applyEdits(ops)    → { applied, errors } — commits ops to the canvas
//   messages           → conversation history [{ role, content }]
//   tier               → 'fast' (Haiku, default) | 'balanced' (Sonnet); sent on
//                        the opening leg only — resume legs inherit it from the
//                        signed session
//   onChunk({ text, fullText }) → streaming text delta of the current round
//   onRound()          → a new model round started; clear live text
//   onToolTrace({ name, args, result }) → a tool just ran (for UI trace chips)
//   control            → optional {} owned by the caller; we attach
//                        control.abort() — aborts the in-flight SSE leg and
//                        refuses to start the next one, so the server loop
//                        stops (no more model rounds). The promise rejects
//                        with err.aborted = true.
//
// returns: Promise<{ reply, toolCallsUsed, driver }>
function sendMessageStream(args) {
  var getWorkflowState = args.getWorkflowState;
  var applyEdits = args.applyEdits;
  var generateCustomNode = args.generateCustomNode;
  var messages = args.messages || [];
  var onChunk = typeof args.onChunk === 'function' ? args.onChunk : null;
  var onRound = typeof args.onRound === 'function' ? args.onRound : null;
  var onToolTrace = typeof args.onToolTrace === 'function' ? args.onToolTrace : null;

  if (typeof getWorkflowState !== 'function') {
    return Promise.reject(new Error('workflowCopilot: getWorkflowState function required'));
  }

  var wireMessages = messages
    .filter(function(m) { return m && m.role && m.content; })
    .map(function(m) { return { role: m.role, content: String(m.content) }; });

  var ctx = {
    getWorkflowState: getWorkflowState,
    applyEdits: applyEdits,
    generateCustomNode: generateCustomNode,
    editNodeWithAskCode: args.editNodeWithAskCode,
    getAskCodeEditorState: args.getAskCodeEditorState,
    // Live variable values (last run / previews) — lets read tools report each
    // variable's CURRENT value, not just its name + type.
    getVariableValues: args.getVariableValues,
    captureCanvas: args.captureCanvas
  };

  return new Promise(function(resolve, reject) {
    var rounds = 0;
    var aborted = false;
    var currentLegAbort = null;

    if (args.control && typeof args.control === 'object') {
      args.control.abort = function() {
        if (aborted) return;
        aborted = true;
        if (currentLegAbort) { try { currentLegAbort(); } catch (e) {} }
        var err = new Error('Copilot stopped by user.');
        err.aborted = true;
        reject(err);
      };
    }

    function streamLeg(path, body) {
      if (aborted) return;
      var settled = false;
      currentLegAbort = apiClient.requestStream(path, { method: 'POST', body: body }, {
        onEvent: function(ev) {
          if (!ev || settled) return;
          if (ev.type === 'round') { if (onRound) onRound(); }
          else if (ev.type === 'chunk') { if (onChunk) onChunk({ text: ev.text, fullText: ev.fullText }); }
          else if (ev.type === 'result') { settled = true; handleResult(ev.result); }
          else if (ev.type === 'error') { settled = true; reject(new Error(ev.message || 'Copilot chat failed')); }
        },
        onError: function(err) { if (!settled) { settled = true; reject(err); } },
        onClose: function() { if (!settled) { settled = true; reject(new Error('workflowCopilot: stream closed before a result')); } }
      });
    }

    function handleResult(current) {
      if (aborted) return;
      if (current && current.kind === 'pending_client_tool') {
        if (rounds++ > MAX_CLIENT_TOOL_ROUNDS) {
          reject(new Error('workflowCopilot: exceeded client tool round limit'));
          return;
        }
        var pending = current.pendingClientTool || {};
        copilotTools.runClientTool(pending.name, pending.args || {}, ctx).then(function(result) {
          if (aborted) return; // stopped while the tool ran — do not resume
          if (onToolTrace) {
            try { onToolTrace({ name: pending.name, args: pending.args, result: result }); }
            catch (e) { /* tracing failures must not break the loop */ }
          }
          streamLeg(ENDPOINT_RESUME_STREAM, {
            session: current.session,
            toolResult: result,
            // Fresh snapshot AFTER the tool ran, so the next round's overview
            // includes the mutation the tool just made.
            workflow: copilotTools.compressForCopilot(getWorkflowState())
          });
        }).catch(reject);
        return;
      }
      if (current && current.kind === 'final') {
        resolve({
          reply: current.reply || '',
          toolCallsUsed: current.toolCallsUsed || 0,
          driver: current.driver || ''
        });
        return;
      }
      reject(new Error('workflowCopilot: unexpected response shape'));
    }

    streamLeg(ENDPOINT_CHAT_STREAM, {
      workflow: copilotTools.compressForCopilot(getWorkflowState()),
      messages: wireMessages,
      tier: args.tier === 'balanced' ? 'balanced' : 'fast'
    });
  });
}

module.exports = {
  sendMessageStream: sendMessageStream
};
